/**
 * WHICH FACE ENGINE THE SERVER'S GALLERY BELONGS TO — the one definition.
 *
 * A face template is only comparable with templates made by the SAME model on
 * the SAME preprocessing. Cosine between two different engines' embeddings is
 * noise, not a weaker signal, so the id names the whole pipeline and not just
 * the network file:
 *
 *   mobilefacenet-192     the network over an UNALIGNED square crop of the
 *                         detector box.
 *   mobilefacenet-192-a4  the same network over a 112×112 crop ALIGNED to the
 *                         ArcFace destination points by a 4-point similarity
 *                         (both eyes + both mouth corners, nose excluded).
 *
 * The same string is written in `FaceCore.MODEL_ID` on the device and
 * `FIELD_FACE_MODEL_ID` in the web embedder; a test on each side pins it to
 * this file.
 */
export const FACE_MODEL_ID = 'mobilefacenet-192-a4';

/**
 * ENGINES THAT ARE NO LONGER ENROLLED ON, AND ARE STILL CARRIED.
 *
 * These two are not the same statement, and conflating them is what the
 * rejected cutover did. `mobilefacenet-192` may not receive a new template
 * from any path — every enrolment writes {@link FACE_MODEL_ID} — but 175 real
 * workers have no other template, and their enrolment photographs do not exist
 * (photo storage landed in field 3.2.72, after they were captured). Nothing
 * can re-embed them server-side. Retiring the id retires those people.
 *
 * So the server SERVES these ids alongside the current one, to a device that
 * can compute a probe in each space, and the device compares each probe only
 * against templates of its own id — see `field.galleryModels.ts` and
 * `FaceGalleries.java`. The list empties itself: a worker recognised on a
 * legacy template gets an aligned one written from the same frame, and when
 * the last legacy ACTIVE row is retired the server stops shipping any, the
 * device's legacy gallery arrives empty, and the second inference stops.
 *
 * A device that declares NO engine (an APK older than the declaration) is
 * treated as the first of these — it is running the retired pipeline, so it
 * gets the legacy templates and nothing else, which is exactly what it can
 * use.
 *
 * `docs/face-model-migration.md` is the whole story.
 */
export const LEGACY_FACE_MODEL_IDS: readonly string[] = ['mobilefacenet-192'];

/**
 * Every engine a device on {@link FACE_MODEL_ID} may hold a gallery for, best
 * first. The ORDER is load-bearing: index 0 is the engine a probe is preferred
 * to be judged in, and the rest are carried only while templates of theirs are
 * still ACTIVE somewhere.
 */
export const CARRIED_FACE_MODEL_IDS: readonly string[] = [
  FACE_MODEL_ID,
  ...LEGACY_FACE_MODEL_IDS,
];

/** Dimensionality of every engine above. */
export const FACE_EMBEDDING_DIM = 192;

/**
 * THE MATCH THRESHOLD THE FIELD ENGINE WILL RUN WITH — the same bounds as the
 * APK's `FacePolicyBounds` (MIN_THRESHOLD / MAX_THRESHOLD). The device clamps
 * whatever it is sent into this range, so a server that judged punches on the
 * raw setting (0.60 by default) was judging them on a number no tablet used.
 * `resolveFacePolicy` clamps with these so the server and the tablet agree.
 */
export const FIELD_MATCH_THRESHOLD_MIN = 0.7;
export const FIELD_MATCH_THRESHOLD_MAX = 0.85;

/** A header or body value this server recognises as an engine id at all. */
export function isKnownFaceModelId(id: unknown): id is string {
  return typeof id === 'string' && (id === FACE_MODEL_ID || LEGACY_FACE_MODEL_IDS.includes(id));
}

